export type DiffCellType = 'same' | 'removed' | 'added';

export interface DiffCell {
  text: string;
  type: DiffCellType;
}

/** One aligned row of a side-by-side diff; a missing side means the line exists on one side only. */
export interface DiffRow {
  left: DiffCell | null;
  right: DiffCell | null;
}

const splitLines = (value: string) => value.replace(/\r\n/g, '\n').split('\n');

/** Line-level diff (LCS) of `before` against `after`, aligned for side-by-side display. */
export const diffLines = (before: string, after: string): DiffRow[] => {
  const a = splitLines(before);
  const b = splitLines(after);

  // lcs[i][j] = length of the longest common subsequence of a[i..] and b[j..]
  const lcs: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0)
  );
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const rows: DiffRow[] = [];
  let removed: DiffCell[] = [];
  let added: DiffCell[] = [];

  // Pair a run of removed lines with the added lines that replace it.
  const flush = () => {
    const size = Math.max(removed.length, added.length);
    for (let k = 0; k < size; k += 1) {
      rows.push({ left: removed[k] ?? null, right: added[k] ?? null });
    }
    removed = [];
    added = [];
  };

  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) {
      flush();
      rows.push({
        left: { text: a[i], type: 'same' },
        right: { text: b[j], type: 'same' },
      });
      i += 1;
      j += 1;
    } else if (j >= b.length || (i < a.length && lcs[i + 1][j] >= lcs[i][j + 1])) {
      removed.push({ text: a[i], type: 'removed' });
      i += 1;
    } else {
      added.push({ text: b[j], type: 'added' });
      j += 1;
    }
  }
  flush();

  return rows;
};

export const countDiff = (rows: DiffRow[]) => ({
  removed: rows.filter((row) => row.left?.type === 'removed').length,
  added: rows.filter((row) => row.right?.type === 'added').length,
});
